import { useCallback } from 'react';
import {
  Button,
  ButtonSizes,
  ButtonVariants,
  Form,
  Typography,
  TypographyColors,
  TypographyVariants,
  TypographyWeights,
} from '@carboledger/ui';
import { TextField, GeneratedIdField } from '@/components/common';
import { useTranslations } from '@/hooks';
import { siteValidation } from '../../sitesValidation';
import { TERMINAL_FIELDS, terminalFieldName } from '../../constants/sites.constants';
import { SiteCountryField } from './SiteCountryField';
import styles from './TerminalFieldset.module.scss';

/**
 * Props:
 *   terminalKey  addresses this terminal's fields inside the flat form values
 *   heading      the card title, e.g. "Terminal-1"
 *   canRemove    false on the last one — a site keeps at least one terminal
 *   onRemove     (terminalKey) => void
 */
export const TerminalFieldset = ({ terminalKey, heading, canRemove, onRemove }) => {
  const t = useTranslations();

  const handleRemove = useCallback(() => onRemove(terminalKey), [onRemove, terminalKey]);

  const field = useCallback((name) => terminalFieldName(terminalKey, name), [terminalKey]);

  return (
    <div className={styles.terminal}>
      <div className={styles.header}>
        <Typography
          variant={TypographyVariants.bodySm}
          weight={TypographyWeights.semibold}
          color={TypographyColors.secondary}
        >
          {heading}
        </Typography>

        <Button
          variant={ButtonVariants.tertiary}
          size={ButtonSizes.sm}
          disabled={!canRemove}
          onClick={handleRemove}
        >
          {t('COMMON_REMOVE')}
        </Button>
      </div>

      <div className={styles.fields}>
        <Form.Row columns={3}>
          <TextField
            required
            className={styles.wide}
            name={field(TERMINAL_FIELDS.terminalName)}
            label={t('FIELD_TERMINAL_NAME')}
            placeholder={t('FORM_TERMINAL_NAME_PLACEHOLDER')}
            rules={siteValidation.terminalName}
          />
          <GeneratedIdField
            name={field(TERMINAL_FIELDS.terminalId)}
            label={t('FIELD_TERMINAL_ID')}
            placeholder={t('FORM_TERMINAL_ID_PLACEHOLDER')}
          />
        </Form.Row>

        <Form.Row columns={3}>
          <TextField
            required
            className={styles.wide}
            name={field(TERMINAL_FIELDS.addressLine1)}
            label={t('FORM_ADDRESS_LINE_1')}
            placeholder={t('FORM_ADDRESS_LINE_1_PLACEHOLDER')}
          />
          <TextField
            name={field(TERMINAL_FIELDS.addressLine2)}
            label={t('FORM_ADDRESS_LINE_2')}
            placeholder={t('FORM_ADDRESS_LINE_2_PLACEHOLDER')}
          />
        </Form.Row>

        <Form.Row columns={3}>
          <TextField
            required
            name={field(TERMINAL_FIELDS.city)}
            label={t('FORM_CITY_NAME')}
            placeholder={t('FORM_CITY_NAME_PLACEHOLDER')}
          />
          <SiteCountryField name={field(TERMINAL_FIELDS.country)} />
          <TextField
            required
            name={field(TERMINAL_FIELDS.zipCode)}
            label={t('FORM_ZIP_CODE')}
            placeholder={t('FORM_ZIP_CODE_PLACEHOLDER')}
          />
        </Form.Row>
      </div>
    </div>
  );
};
